import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, Phone, Mail, MessageSquare, Users, Upload, Building2, X, AlertCircle } from 'lucide-react';
import { getCustomers, Customer, checkDuplicateCustomer, createCustomer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import EmptyState from '../components/EmptyState';
import CustomerModal from '../components/CustomerModal';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import PermissionGuard from '../components/PermissionGuard';
import { toast } from 'sonner';


function Customers() {
  const { selectedBusiness } = useBusiness();
  const [searchQuery, setSearchQuery] = useState('');
  const [isNewCustomerModalOpen, setIsNewCustomerModalOpen] = useState(false);
  const [isCustomerDetailsModalOpen, setIsCustomerDetailsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStats, setImportStats] = useState({ total: 0, imported: 0, duplicates: 0, errors: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvDelimiter, setCsvDelimiter] = useState(',');
  const [importCompleted, setImportCompleted] = useState(false);

  const loadCustomers = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getCustomers(selectedBusiness.id);
      setCustomers(data);
      setError(null);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [selectedBusiness?.id]);

  const filteredCustomers = customers.filter((customer) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      customer.first_name?.toLowerCase().includes(searchLower) ||
      customer.last_name?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower) ||
      customer.phone?.toLowerCase().includes(searchLower)
    );
  });

  // Function to download a sample CSV file
  const downloadSampleCSV = () => {
    const sampleData = [
      'first_name,last_name,email,phone,address,status,notes',
      'John,Doe,john.doe@example.com,555-123-4567,"123 Main St, Anytown, USA",active,"First customer"',
      'Jane,Smith,jane.smith@example.com,555-987-6543,"456 Oak St, Somewhere, USA",active,""'
    ].join('\n');
    
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'sample_customers.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Helper function to detect delimiter
  const detectDelimiter = (text: string): string => {
    const firstLine = text.split('\n')[0];
    
    // Check common delimiters
    const commaCount = (firstLine.match(/,/g) || []).length;
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    const tabCount = (firstLine.match(/\t/g) || []).length;
    
    if (semicolonCount > commaCount && semicolonCount > tabCount) return ';';
    if (tabCount > commaCount && tabCount > semicolonCount) return '\t';
    return ','; // Default to comma
  };

  // Helper function to parse CSV row with possible quoted values
  const parseCSVRow = (row: string, delimiter: string): string[] => {
    const result: string[] = [];
    let inQuote = false;
    let currentValue = '';
    
    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      
      if (char === '"') {
        if (i < row.length - 1 && row[i + 1] === '"') {
          // Handle escaped quotes (double quotes)
          currentValue += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuote = !inQuote;
        }
      } else if (char === delimiter && !inQuote) {
        result.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    
    // Add the last value
    result.push(currentValue.trim());
    console.log('Parsed row:', result);
    return result;
  };

  // Simpler CSV row parser as fallback
  const simpleSplit = (row: string, delimiter: string): string[] => {
    return row.split(delimiter).map(val => val.trim());
  };

  // Simple, direct import modal handler (no unnecessary wrapping function)
  const toggleImportModal = () => {
    // If we're closing the modal, reset all import-related state
    if (isImportModalOpen) {
      setImportFile(null);
      setImportProgress(0);
      setImportStats({ total: 0, imported: 0, duplicates: 0, errors: 0 });
      setIsImporting(false);
      setImportCompleted(false);
      setCsvDelimiter(',');
      
      // Also refresh the customer list to show any newly imported customers
      loadCustomers();
    }
    
    // Toggle modal state
    setIsImportModalOpen(!isImportModalOpen);
    console.log('Import modal toggled, new state:', !isImportModalOpen);
  };

  // Update the file input handling to use a direct reference
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      console.log('File selected:', file.name, 'Size:', file.size, 'Type:', file.type);
      setImportFile(file);
    } else {
      setImportFile(null);
    }
  };

  // Function to parse CSV and import customers
  async function handleImportCustomers() {
    console.log('🔄 [IMPORT] handleImportCustomers called', { 
      importFile: importFile?.name, 
      size: importFile?.size,
      type: importFile?.type,
      selectedBusiness: selectedBusiness?.id 
    });
    
    if (!importFile) {
      console.error('❌ [IMPORT] No file selected');
      toast.error('Please select a CSV file');
      return;
    }
    
    if (!selectedBusiness?.id) {
      console.error('❌ [IMPORT] No business selected');
      toast.error('No business selected');
      return;
    }
    
    try {
      // Reset state for new import
      setImportProgress(0);
      setImportStats({ total: 0, imported: 0, duplicates: 0, errors: 0 });
      setIsImporting(true);
      setImportCompleted(false);
      
      console.log('Starting import process for file:', importFile.name);
      
      // Read the file content
      console.clear(); // Clear console for better debugging
      console.log('🔄 [IMPORT] Reading file content...');
      alert('Import started - check console for detailed logs!');
      const reader = new FileReader();
      
      const fileContent = await new Promise<string>((resolve, reject) => {
        reader.onload = (event) => {
          console.log('✅ [IMPORT] File read successfully');
          if (event.target?.result) {
            resolve(event.target.result as string);
          } else {
            console.error('❌ [IMPORT] No file content in result');
            reject(new Error('Failed to read file content'));
          }
        };
        reader.onerror = (error) => {
          console.error('❌ [IMPORT] FileReader error:', error);
          reject(new Error('Error reading file'));
        };
        reader.readAsText(importFile, 'UTF-8');
      });
      
      console.log('File content loaded, length:', fileContent.length);
      
      if (fileContent.trim().length === 0) {
        toast.error('The CSV file is empty');
        setIsImporting(false);
        setImportCompleted(true);
        setImportStats(prev => ({ ...prev, errors: 1 }));
        return;
      }
      
      // Wrap parsing logic in additional try/catch for better error messages
      try {
        // Split into rows - handle both CR, LF, and CRLF line endings
        const rows = fileContent.split(/\r?\n/).filter(line => line.trim() !== '');
        
        if (rows.length < 1) {
          toast.error('CSV file must have at least one data row');
          setIsImporting(false);
          setImportCompleted(true);
          setImportStats(prev => ({ ...prev, errors: 1 }));
          return;
        }
        
        // Skip direct parser for complex CSV files - use header-based parsing instead
        console.log('🚫 [IMPORT] Skipping direct parser - using header-based parsing for complex CSV');
        const directParseResults: any[] = []; // Force empty to skip direct parsing
        
        if (false) { // Disabled
          console.log('Successfully parsed with direct method:', directParseResults.length, 'customers');
          setImportStats(prev => ({ ...prev, total: directParseResults.length }));
          
          // Process the customers
          const batchSize = 5;
          let imported = 0;
          let duplicates = 0;
          let errors = 0;
          
          for (let i = 0; i < directParseResults.length; i += batchSize) {
            const batch = directParseResults.slice(i, i + batchSize);
            
            for (const customer of batch) {
                          try {
              console.log(`🔄 [IMPORT] Processing customer ${i + 1}/${directParseResults.length}:`, {
                first_name: customer.first_name,
                last_name: customer.last_name,
                email: customer.email,
                phone: customer.phone
              });

              // Check for duplicates - sequential approach
              console.log('🔍 [IMPORT] Checking for duplicates (sequential)...');
              
              let isDuplicate = false;
              let matchingCustomer = null;
              let matchingField = '';
              
              // Step 1: Check phone number first (if provided)
              if (customer.phone?.trim()) {
                console.log('📱 [IMPORT] Checking phone number:', customer.phone);
                const phoneCheck = await checkDuplicateCustomer(
                  selectedBusiness.id,
                  null, // no email check
                  customer.phone
                );
                
                if (phoneCheck.isDuplicate) {
                  isDuplicate = true;
                  matchingCustomer = phoneCheck.matchingCustomer;
                  matchingField = 'phone';
                  console.log('⚠️ [IMPORT] Phone number already exists in system');
                }
              }
              
              // Step 2: If phone didn't match, check email (if provided)
              if (!isDuplicate && customer.email?.trim()) {
                console.log('📧 [IMPORT] Checking email address:', customer.email);
                const emailCheck = await checkDuplicateCustomer(
                  selectedBusiness.id,
                  customer.email,
                  null // no phone check
                );
                
                if (emailCheck.isDuplicate) {
                  isDuplicate = true;
                  matchingCustomer = emailCheck.matchingCustomer;
                  matchingField = 'email';
                  console.log('⚠️ [IMPORT] Email address already exists in system');
                }
              }
              
              if (isDuplicate) {
                console.log('⚠️ [IMPORT] Duplicate customer detected:', {
                  new_customer: `${customer.first_name} ${customer.last_name}`,
                  existing_customer: `${matchingCustomer?.first_name} ${matchingCustomer?.last_name}`,
                  matching_field: matchingField,
                  matching_value: matchingField === 'phone' ? customer.phone : customer.email
                });
                duplicates++;
                setImportStats(prev => ({ ...prev, duplicates }));
                continue;
              }
              
              console.log('✅ [IMPORT] No duplicates found, creating customer...');
              
              // Create the customer
              const customerData = {
                business_id: selectedBusiness.id,
                type: 'person' as const,
                first_name: customer.first_name,
                last_name: customer.last_name, 
                email: customer.email,
                phone: customer.phone,
                address: null,
                status: 'active' as const,
                amount_spent: 0,
                credit_balance: 0,
                tags: [],
                preferences: {},
                tax_exempt: false,
                workflow_exempt: false,
                carfax_exempt: false,
                receive_reminders: true,
                receive_offers: true,
                is_supplier: false,
                lifetime_value: 0,
                source: null,
                last_interaction: null,
                next_service_date: null,
                referral_source: null,
                notes: null,
                repeat: false
              };
              
              console.log('📝 [IMPORT] Customer data prepared:', customerData);
              
              await createCustomer(customerData);
              
              console.log('✅ [IMPORT] Successfully imported customer:', customer.first_name, customer.last_name);
              imported++;
              setImportStats(prev => ({ ...prev, imported }));
            } catch (error) {
              console.error('❌ [IMPORT] Error importing customer:', {
                customer: `${customer.first_name} ${customer.last_name}`,
                email: customer.email,
                phone: customer.phone,
                error: error instanceof Error ? error.message : error,
                stack: error instanceof Error ? error.stack : undefined
              });
              errors++;
              setImportStats(prev => ({ ...prev, errors }));
            }
            }
            
            const progress = Math.min(100, Math.round(((i + batch.length) / directParseResults.length) * 100));
            setImportProgress(progress);
            
            // Short delay to allow UI to update
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          
          // Final update
          setImportStats({ total: directParseResults.length, imported, duplicates, errors });
          setImportProgress(100);
          
          // Show success message
          if (imported > 0) {
            toast.success(`Imported ${imported} customers successfully`);
          } else if (duplicates > 0 && errors === 0) {
            toast.info('All customers already exist in the system');
          } else if (errors > 0) {
            toast.error(`Failed to import ${errors} customers`);
          }
          
          // Refresh customer list
          await loadCustomers();
          
          // Always show the import summary
          setIsImporting(false);
          setImportCompleted(true);
          return;
        }
        
        // If direct parsing didn't work, continue with the regular approach
        // Detect delimiter if not explicitly set
        const detectedDelimiter = detectDelimiter(fileContent);
        const delimiter = csvDelimiter || detectedDelimiter;
        
        console.log('Using delimiter:', delimiter === ',' ? 'comma' : delimiter === ';' ? 'semicolon' : 'tab');
        
        // Try parsing header with the complex parser, fall back to simple if needed
        let headerRow;
        try {
          headerRow = parseCSVRow(rows[0], delimiter);
        } catch (e) {
          console.error('Error parsing header with complex parser, using fallback:', e);
          headerRow = simpleSplit(rows[0], delimiter);
        }
        
        console.log('🎯 [CSV PARSE] Raw header row:', headerRow);
        console.log('🎯 [CSV PARSE] Header row length:', headerRow.length);
        
        // Map standard column names to indices
        const columnMap: Record<string, number> = {};
        
        // Check for specific CRM export format first
        const headerString = headerRow.join(',').toLowerCase();
        console.log('🔍 [CSV PARSE] Header string for detection:', headerString);
        console.log('🔍 [CSV PARSE] Looking for: contact id, first name, last name');
        
        if (headerString.includes('contact id') && headerString.includes('first name') && headerString.includes('last name')) {
          console.log('🎯 [CSV PARSE] ✅ CRM export format DETECTED - using hardcoded mapping');
          // Hardcoded mapping for CRM export format:
          // "Contact Id","First Name","Last Name","Business Name","Company Name","Phone","Email","Created","Last Activity","Tags","Additional Emails","Additional Phones"
          columnMap.contact_id = 0;
          columnMap.first_name = 1;
          columnMap.last_name = 2;
          columnMap.business_name = 3;
          columnMap.company_name = 4;
          columnMap.phone = 5;
          columnMap.email = 6;
          console.log('📱 [CSV PARSE] CRM Format - Phone mapped to column 5');
          console.log('📧 [CSV PARSE] CRM Format - Email mapped to column 6');
          console.log('🗺️ [CSV PARSE] Final column mapping:', columnMap);
        } else {
          console.log('❌ [CSV PARSE] CRM format NOT detected, using dynamic mapping');
          // Enhanced header mapping for various CSV formats
          headerRow.forEach((header, index) => {
            const normalizedHeader = header.toLowerCase().trim().replace(/['"]/g, ''); // Remove quotes
            
            console.log(`🔍 [CSV PARSE] Header ${index}: "${header}" -> "${normalizedHeader}"`);
            
            // Check for common column name patterns
            if (normalizedHeader.includes('first') && normalizedHeader.includes('name')) {
              columnMap.first_name = index;
              console.log(`✅ [CSV PARSE] Mapped first_name to column ${index}`);
            } else if (normalizedHeader.includes('last') && normalizedHeader.includes('name')) {
              columnMap.last_name = index;
              console.log(`✅ [CSV PARSE] Mapped last_name to column ${index}`);
            } else if (normalizedHeader === 'email' || normalizedHeader.includes('email')) {
              columnMap.email = index;
              console.log(`✅ [CSV PARSE] Mapped email to column ${index}`);
            } else if (normalizedHeader === 'phone' || normalizedHeader.includes('phone')) {
              columnMap.phone = index;
              console.log(`✅ [CSV PARSE] Mapped phone to column ${index}`);
            } else if (normalizedHeader.includes('address')) {
              columnMap.address = index;
              console.log(`✅ [CSV PARSE] Mapped address to column ${index}`);
            } else if (normalizedHeader.includes('status')) {
              columnMap.status = index;
              console.log(`✅ [CSV PARSE] Mapped status to column ${index}`);
            } else if (normalizedHeader.includes('note')) {
              columnMap.notes = index;
              console.log(`✅ [CSV PARSE] Mapped notes to column ${index}`);
            } else if (normalizedHeader.includes('business') && normalizedHeader.includes('name')) {
              columnMap.business_name = index;
              console.log(`✅ [CSV PARSE] Mapped business_name to column ${index}`);
            } else if (normalizedHeader.includes('company') && normalizedHeader.includes('name')) {
              columnMap.company_name = index;
              console.log(`✅ [CSV PARSE] Mapped company_name to column ${index}`);
            }
          });
        }
        
        console.log('Column mapping:', columnMap);
        
        // If we don't have proper column mapping, try to detect if this is a simple name,email format
        // Like: Name,email,phone
        if (!('first_name' in columnMap) || !('last_name' in columnMap)) {
          console.log('Standard columns not found, trying alternative format detection');
          
          // Check if we have rows with email addresses in them
          let emailColumnIndex = -1;
          let nameColumnIndex = -1;
          let phoneColumnIndex = -1;
          
          // Look at the first few data rows to detect patterns
          for (let i = 1; i < Math.min(rows.length, 5); i++) {
            const columns = parseCSVRow(rows[i], delimiter);
            for (let j = 0; j < columns.length; j++) {
              const value = columns[j].toLowerCase().trim();
              if (value.includes('@') && value.includes('.')) {
                emailColumnIndex = j;
              } else if (value.includes('604-') || value.includes('778-') || value.match(/\d{3}-\d{3}-\d{4}/)) {
                phoneColumnIndex = j;
              }
            }
            if (emailColumnIndex !== -1) break;
          }
          
          // If we found an email column, assume the format is name,email,phone
          // or name,email (comma delimited)
          if (emailColumnIndex !== -1) {
            console.log('Detected alternative format with email at index', emailColumnIndex);
            // Assume name is before email
            nameColumnIndex = emailColumnIndex > 0 ? emailColumnIndex - 1 : -1;
            
            // If we couldn't find a phone column but there's a column after email, assume it's phone
            if (phoneColumnIndex === -1 && emailColumnIndex < headerRow.length - 1) {
              phoneColumnIndex = emailColumnIndex + 1;
            }
            
            // Update our column map
            columnMap.combined_name_email = emailColumnIndex;
            if (phoneColumnIndex !== -1) {
              columnMap.phone = phoneColumnIndex;
            }
          }
        }
        
        // Handle special case where the file doesn't have a header row
        // and is just data with name,email,phone format
        if (Object.keys(columnMap).length === 0 && rows.length > 0) {
          // Check if first row looks like data rather than headers
          const firstRow = parseCSVRow(rows[0], delimiter);
          let hasEmailInFirstRow = false;
          
          for (const cell of firstRow) {
            if (cell.includes('@') && cell.includes('.')) {
              hasEmailInFirstRow = true;
              break;
            }
          }
          
          if (hasEmailInFirstRow) {
            console.log('File appears to have no header row, using first row for format detection');
            for (let j = 0; j < firstRow.length; j++) {
              const value = firstRow[j].toLowerCase().trim();
              if (value.includes('@') && value.includes('.')) {
                columnMap.combined_name_email = j;
              } else if (value.includes('604-') || value.includes('778-') || value.match(/\d{3}-\d{3}-\d{4}/)) {
                columnMap.phone = j;
              }
            }
          }
        }
        
        console.log('🗺️ [CSV PARSE] Final column mapping:', columnMap);
        
        // Show a sample row to verify column alignment
        if (rows.length > 1) {
          const sampleRow = parseCSVRow(rows[1], delimiter);
          console.log('📋 [CSV PARSE] Sample data row:', sampleRow);
          console.log('📋 [CSV PARSE] Column analysis:');
          sampleRow.forEach((value, index) => {
            console.log(`  Column ${index}: "${value}"`);
          });
          
          if ('phone' in columnMap) {
            console.log(`📱 [CSV PARSE] Phone column ${columnMap.phone} contains: "${sampleRow[columnMap.phone]}"`);
          }
          if ('email' in columnMap) {
            console.log(`📧 [CSV PARSE] Email column ${columnMap.email} contains: "${sampleRow[columnMap.email]}"`);
          }
        }
        
        // Ensure we have at least one way to identify customers
        if (!('first_name' in columnMap) && !('combined_name_email' in columnMap)) {
          toast.error('CSV format not recognized. Please ensure it contains name and email columns.');
          setIsImporting(false);
          setImportCompleted(true);
          setImportStats(prev => ({ ...prev, errors: 1 }));
          return;
        }
        
        // Process data rows - skip header
        const customersToImport = [];
        for (let i = 1; i < rows.length; i++) {
          if (!rows[i].trim()) continue; // Skip empty rows
          
          try {
            const columns = parseCSVRow(rows[i], delimiter);
            // Skip row if it doesn't have enough columns
            if (columns.length === 0) {
              console.log('Skipping empty row');
              continue;
            }
            
            let firstName = '';
            let lastName = '';
            let email = null;
            let phone = null;
            
            // Handle the standard column format
            if ('first_name' in columnMap && 'last_name' in columnMap) {
              firstName = columns[columnMap.first_name]?.trim() || '';
              lastName = columns[columnMap.last_name]?.trim() || '';
              
              // If first/last name are empty, try business/company names
              if (!firstName && !lastName) {
                if ('business_name' in columnMap && columns[columnMap.business_name]?.trim()) {
                  const businessName = columns[columnMap.business_name].trim();
                  firstName = businessName;
                  lastName = '(Business)';
                  console.log(`📢 [CSV PARSE] Using business name: ${businessName}`);
                } else if ('company_name' in columnMap && columns[columnMap.company_name]?.trim()) {
                  const companyName = columns[columnMap.company_name].trim();
                  firstName = companyName;
                  lastName = '(Company)';
                  console.log(`🏢 [CSV PARSE] Using company name: ${companyName}`);
                } else {
                  console.log('⚠️ [CSV PARSE] Skipping row due to missing name data:', rows[i]);
                  continue;
                }
              }
              
              // If only one name is provided, split or use as-is
              if (!firstName && lastName) {
                firstName = lastName;
                lastName = '(Name)';
              } else if (firstName && !lastName) {
                // Try to split if it contains spaces
                const nameParts = firstName.split(' ');
                if (nameParts.length > 1) {
                  firstName = nameParts[0];
                  lastName = nameParts.slice(1).join(' ');
                } else {
                  lastName = '(Single Name)';
                }
              }
              
              email = 'email' in columnMap && columns[columnMap.email] ? columns[columnMap.email].trim() || null : null;
              phone = 'phone' in columnMap && columns[columnMap.phone] ? columns[columnMap.phone].trim() || null : null;
            } 
            // Handle the combined name/email format
            else if ('combined_name_email' in columnMap) {
              const emailValue = columns[columnMap.combined_name_email]?.trim() || '';
              
              if (!emailValue || !emailValue.includes('@')) {
                console.log('Skipping row due to missing or invalid email:', rows[i]);
                continue;
              }
              
              // Extract email from the combined field
              email = emailValue;
              
              // Extract name from beginning of the line
              // Format appears to be: Name,email
              if (columnMap.combined_name_email > 0) {
                const nameValue = columns[columnMap.combined_name_email - 1]?.trim() || '';
                
                // Try to split name into first and last
                const nameParts = nameValue.split(',');
                if (nameParts.length > 1) {
                  firstName = nameParts[0].trim();
                  lastName = nameParts[1].trim();
                } else {
                  // If there's no comma, try to split on space
                  const spaceParts = nameValue.split(/\s+/);
                  if (spaceParts.length > 1) {
                    firstName = spaceParts[0].trim();
                    lastName = spaceParts.slice(1).join(' ').trim();
                  } else {
                    // If all else fails, use the whole value as first name
                    firstName = nameValue;
                    lastName = '(No Last Name)';
                  }
                }
              } else {
                // If there's no name column, extract from email
                firstName = email.split('@')[0];
                lastName = '(From Email)';
              }
              
              // Get phone if available
              phone = 'phone' in columnMap && columns[columnMap.phone] ? columns[columnMap.phone].trim() || null : null;
            } 
            // Special case: combined name and email in one column (like "name,email@example.com")
            else {
              // Try to find an email in any column
              let foundEmail = false;
              
              for (let j = 0; j < columns.length; j++) {
                const value = columns[j].trim();
                if (value.includes('@') && value.includes('.')) {
                  email = value;
                  foundEmail = true;
                  
                  // If this is a line like "name,email@example.com,phone"
                  // Try to extract name from same column
                  const commaIdx = value.indexOf(',');
                  if (commaIdx > 0 && commaIdx < value.indexOf('@')) {
                    const namePart = value.substring(0, commaIdx).trim();
                    const emailPart = value.substring(commaIdx + 1).trim();
                    
                    email = emailPart;
                    const nameParts = namePart.split(/\s+/);
                    if (nameParts.length > 1) {
                      firstName = nameParts[0].trim();
                      lastName = nameParts.slice(1).join(' ').trim();
                    } else {
                      firstName = namePart;
                      lastName = '(No Last Name)';
                    }
                  } else {
                    // Just use email as identifier
                    firstName = email.split('@')[0];
                    lastName = '(From Email)';
                  }
                  
                  // Try to find phone in another column
                  for (let k = 0; k < columns.length; k++) {
                    if (k !== j) {
                      const phoneCandidate = columns[k].trim();
                      if (phoneCandidate.includes('-') || /^\d+$/.test(phoneCandidate)) {
                        phone = phoneCandidate;
                        break;
                      }
                    }
                  }
                  
                  break;
                }
              }
              
              if (!foundEmail) {
                console.log('Skipping row due to no email found:', rows[i]);
                continue;
              }
            }
            
            // Handle special case for the file shown in screenshot
            // Format appears to be: Name,email,phone with no header row
            if (rows.length > 10 && i < 10 && !phone && columns.length >= 2) {
              const line = rows[i];
              // Check if it matches pattern like: Name,email@example.com,604-xxx-xxxx
              const emailPhoneMatch = line.match(/([^,]+),([^,]+@[^,]+),([^,]+)/);
              if (emailPhoneMatch) {
                const [_, namePart, emailPart, phonePart] = emailPhoneMatch;
                
                // Update with the extracted values
                if (emailPart.includes('@') && emailPart.includes('.')) {
                  email = emailPart.trim();
                }
                
                if (phonePart.includes('-') || /^\d+$/.test(phonePart)) {
                  phone = phonePart.trim();
                }
                
                const nameParts = namePart.split(/\s+/);
                if (nameParts.length > 1) {
                  firstName = nameParts[0].trim();
                  lastName = nameParts.slice(1).join(' ').trim();
                } else {
                  firstName = namePart.trim();
                  lastName = '(No Last Name)';
                }
              }
            }
            
            // Ensure we have minimum required fields
            if (!firstName || !email) {
              console.log('Skipping row due to missing required fields:', rows[i]);
              continue;
            }
            
            customersToImport.push({
              first_name: firstName,
              last_name: lastName,
              email: email,
              phone: phone,
              address: 'address' in columnMap && columns[columnMap.address] ? columns[columnMap.address].trim() || null : null,
              status: 'status' in columnMap && columns[columnMap.status] ? columns[columnMap.status].trim() as 'active' | 'inactive' : 'active',
              notes: 'notes' in columnMap && columns[columnMap.notes] ? columns[columnMap.notes].trim() || null : null
            });
          } catch (rowError) {
            console.error('Error parsing row:', rows[i], rowError);
            continue;
          }
        }
        
        console.log('Valid customers to import:', customersToImport.length);
        setImportStats(prev => ({ ...prev, total: customersToImport.length }));
        
        if (customersToImport.length === 0) {
          toast.error('No valid customers found in CSV');
          setIsImporting(false);
          setImportCompleted(true);
          setImportStats(prev => ({ ...prev, errors: 1 }));
          return;
        }
        
        // Process customers in batches to show progress
        const batchSize = 5;
        let imported = 0;
        let duplicates = 0;
        let errors = 0;
        
        for (let i = 0; i < customersToImport.length; i += batchSize) {
          const batch = customersToImport.slice(i, i + batchSize);
          
          for (const customer of batch) {
            try {
              console.log(`🔄 [IMPORT] Processing customer:`, {
                first_name: customer.first_name,
                last_name: customer.last_name,
                email: customer.email,
                phone: customer.phone,
                status: customer.status
              });

              // Validation checks
              if (!customer.first_name || !customer.first_name.trim()) {
                throw new Error('First name is required and cannot be empty');
              }
              if (!customer.last_name || !customer.last_name.trim()) {
                throw new Error('Last name is required and cannot be empty');
              }
              if (!customer.email && !customer.phone) {
                throw new Error('Either email or phone number is required');
              }

              // Check for duplicates - sequential approach
              console.log('🔍 [IMPORT] Checking for duplicates (sequential)...');
              
              let isDuplicate = false;
              let matchingCustomer = null;
              let matchingField = '';
              
              // Step 1: Check phone number first (if provided)
              if (customer.phone?.trim()) {
                console.log('📱 [IMPORT] Checking phone number:', customer.phone);
                const phoneCheck = await checkDuplicateCustomer(
                  selectedBusiness.id,
                  null, // no email check
                  customer.phone
                );
                
                if (phoneCheck.isDuplicate) {
                  isDuplicate = true;
                  matchingCustomer = phoneCheck.matchingCustomer;
                  matchingField = 'phone';
                  console.log('⚠️ [IMPORT] Phone number already exists in system');
                }
              }
              
              // Step 2: If phone didn't match, check email (if provided)
              if (!isDuplicate && customer.email?.trim()) {
                console.log('📧 [IMPORT] Checking email address:', customer.email);
                const emailCheck = await checkDuplicateCustomer(
                  selectedBusiness.id,
                  customer.email,
                  null // no phone check
                );
                
                if (emailCheck.isDuplicate) {
                  isDuplicate = true;
                  matchingCustomer = emailCheck.matchingCustomer;
                  matchingField = 'email';
                  console.log('⚠️ [IMPORT] Email address already exists in system');
                }
              }
              
              if (isDuplicate) {
                console.log('⚠️ [IMPORT] Duplicate customer detected:', {
                  new_customer: `${customer.first_name} ${customer.last_name}`,
                  existing_customer: `${matchingCustomer?.first_name} ${matchingCustomer?.last_name}`,
                  matching_field: matchingField,
                  matching_value: matchingField === 'phone' ? customer.phone : customer.email
                });
                duplicates++;
                setImportStats(prev => ({ ...prev, duplicates }));
                continue;
              }
              
              console.log('✅ [IMPORT] No duplicates found, creating customer...');
              
              // Create the customer with explicit properties to avoid undefined values
              const customerData = {
                business_id: selectedBusiness.id,
                type: 'person' as const,
                first_name: customer.first_name.trim(),
                last_name: customer.last_name.trim(),
                email: customer.email?.trim() || null,
                phone: customer.phone?.trim() || null,
                address: customer.address?.trim() || null,
                status: (customer.status?.trim() as 'active' | 'inactive') || 'active',
                amount_spent: 0,
                credit_balance: 0,
                tags: [],
                preferences: {},
                tax_exempt: false,
                workflow_exempt: false,
                carfax_exempt: false,
                receive_reminders: true,
                receive_offers: true,
                is_supplier: false,
                lifetime_value: 0,
                source: null,
                last_interaction: null,
                next_service_date: null,
                referral_source: null,
                notes: customer.notes?.trim() || null,
                repeat: false
              };
              
              console.log('📝 [IMPORT] Customer data prepared:', customerData);
              
              await createCustomer(customerData);
              
              console.log('✅ [IMPORT] Successfully imported customer:', customer.first_name, customer.last_name);
              imported++;
              setImportStats(prev => ({ ...prev, imported }));
            } catch (error) {
              console.error('❌ [IMPORT] Error importing customer:', {
                customer: `${customer.first_name} ${customer.last_name}`,
                email: customer.email,
                phone: customer.phone,
                error: error instanceof Error ? error.message : error,
                stack: error instanceof Error ? error.stack : undefined
              });
              errors++;
              setImportStats(prev => ({ ...prev, errors }));
            }
          }
          
          const progress = Math.min(100, Math.round(((i + batch.length) / customersToImport.length) * 100));
          setImportProgress(progress);
          
          // Short delay to allow UI to update
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        // Final update
        setImportStats({ total: customersToImport.length, imported, duplicates, errors });
        setImportProgress(100);
        
        // Show success message
        if (imported > 0) {
          toast.success(`Imported ${imported} customers successfully`);
        } else if (duplicates > 0 && errors === 0) {
          toast.info('All customers already exist in the system');
        } else if (errors > 0) {
          toast.error(`Failed to import ${errors} customers`);
        }
        
        // Refresh customer list
        await loadCustomers();
        
        // Always show the import summary
        setIsImporting(false);
        setImportCompleted(true);
      } catch (parsingError) {
        // Handle CSV parsing errors specifically
        console.error('Error parsing CSV:', parsingError);
        toast.error('Failed to parse CSV: ' + (parsingError instanceof Error ? parsingError.message : 'Invalid format'));
        setIsImporting(false);
        setImportCompleted(true);
        setImportStats(prev => ({ ...prev, errors: 1 }));
      }
    } catch (error) {
      console.error('❌ [IMPORT] Error processing CSV import:', error);
      
      // Provide specific error messages based on error type
      let errorMessage = 'Failed to import customers: ';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to read file')) {
          errorMessage += 'Could not read the selected file. Please check the file format.';
        } else if (error.message.includes('CSV format not recognized')) {
          errorMessage += 'CSV format not recognized. Please check your file format.';
        } else if (error.message.includes('duplicate')) {
          errorMessage += 'Duplicate customer found: ' + error.message;
        } else if (error.message.includes('Supabase') || error.message.includes('database')) {
          errorMessage += 'Database connection error. Please try again.';
        } else {
          errorMessage += error.message;
        }
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      toast.error(errorMessage);
      setIsImporting(false);
      setImportCompleted(true);
      setImportStats(prev => ({ ...prev, errors: 1 }));
    }
  }

  // Parse CSV text directly (for the specific format in the screenshot)
  const parseSimpleCSVFormat = (text: string): { first_name: string; last_name: string; email: string; phone: string | null }[] => {
    console.log('Trying direct parsing of simple CSV format');
    const results = [];
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
    
    for (const line of lines) {
      // Look for patterns like: Name,email@example.com,phone
      // Or Firstname Lastname,email@example.com,phone
      const match = line.match(/([^,]+),([^,]+@[^,]+),(.*)/);
      
      if (match) {
        const [_, namePart, emailPart, phonePart] = match;
        
        if (!emailPart.includes('@')) continue;
        
        // Extract name parts - could be "First Last" or just "Name"
        let firstName = '';
        let lastName = '';
        
        const nameParts = namePart.split(/\s+/);
        if (nameParts.length > 1) {
          firstName = nameParts[0].trim();
          lastName = nameParts.slice(1).join(' ').trim();
        } else {
          firstName = namePart.trim();
          lastName = '(From Email)';
        }
        
        results.push({
          first_name: firstName,
          last_name: lastName,
          email: emailPart.trim(),
          phone: phonePart.trim() || null
        });
      }
    }
    
    return results;
  };

  return (
    <div className="min-h-screen bg-white" data-customers-page>
      <div className="w-full px-4 sm:px-6 py-4 sm:py-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="space-y-4">
            {/* Title */}
            <h1 className="text-2xl font-semibold text-gray-900">Customers</h1>
            
            {/* Search, Filter, and Actions Row */}
            <div className="flex items-center gap-3">
              {/* Search Bar */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                />
              </div>
              
              {/* Filter Button */}
              <button className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors flex-shrink-0">
                <Filter className="h-4 w-4" />
                <span className="hidden sm:inline">Filter</span>
              </button>
              
              {/* Add Button */}
              <button 
                onClick={() => setIsNewCustomerModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap flex-shrink-0"
              >
                <span className="sm:hidden">Add</span>
                <span className="hidden sm:inline">Add Customer</span>
              </button>
            </div>
          </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            <div className="flex items-center">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              {error}
            </div>
          </div>
        )}

        {/* Main Content Container */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading customers...</p>
              </div>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <EmptyState
                icon={<Users className="w-8 h-8 text-gray-400" />}
                title={searchQuery ? 'No matching customers' : 'No customers yet'}
                description={
                  searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Start adding customers to manage your client base.'
                }
                actionLabel={
                  searchQuery ? 'Clear search' : 'Add your first customer'
                }
                onAction={() => (searchQuery ? setSearchQuery('') : setIsNewCustomerModalOpen(true))}
              />
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="block md:hidden">
                <div className="divide-y divide-gray-200">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      className="p-4 hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setIsCustomerDetailsModalOpen(true);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-base font-medium text-blue-600">
                              {customer.first_name && customer.first_name[0]}
                              {customer.last_name && customer.last_name[0]}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="font-medium text-gray-900 truncate">
                                {customer.first_name} {customer.last_name}
                              </div>
                              {customer.is_supplier && (
                                <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs font-medium px-2 py-1 rounded flex-shrink-0">
                                  <Building2 className="h-3 w-3" />
                                  Supplier
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 truncate mb-1">{customer.email}</div>
                            {customer.phone && (
                              <div className="text-sm text-gray-500 mb-2">{customer.phone}</div>
                            )}
                            <div className="flex items-center justify-between">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                customer.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                              </span>
                              <div className="flex items-center gap-1">
                                <button 
                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Add phone action here
                                  }}
                                  title="Call customer"
                                >
                                  <Phone className="h-4 w-4" />
                                </button>
                                <button 
                                  className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-150"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Add email action here
                                  }}
                                  title="Email customer"
                                >
                                  <Mail className="h-4 w-4" />
                                </button>
                                <button 
                                  className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-150"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Add message action here
                                  }}
                                  title="Message customer"
                                >
                                  <MessageSquare className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden md:block overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCustomers.map((customer) => (
                      <tr
                        key={customer.id}
                        className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setIsCustomerDetailsModalOpen(true);
                        }}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">
                                {customer.first_name && customer.first_name[0]}
                                {customer.last_name && customer.last_name[0]}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <div className="font-medium text-gray-900">
                                  {customer.first_name} {customer.last_name}
                                </div>
                                {customer.is_supplier && (
                                  <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs font-medium px-2 py-1 rounded">
                                    <Building2 className="h-3 w-3" />
                                    Supplier
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{customer.email}</div>
                          {customer.phone && (
                            <div className="text-sm text-gray-500">{customer.phone}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            customer.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button 
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Add phone action here
                              }}
                              title="Call customer"
                            >
                              <Phone className="h-4 w-4" />
                            </button>
                            <button 
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-150"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Add email action here
                              }}
                              title="Email customer"
                            >
                              <Mail className="h-4 w-4" />
                            </button>
                            <button 
                              className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-150"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Add message action here
                              }}
                              title="Message customer"
                            >
                              <MessageSquare className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        </div>
      </div>
      <CustomerModal
        isOpen={isNewCustomerModalOpen}
        onClose={() => setIsNewCustomerModalOpen(false)}
        onSuccess={() => {
          setIsNewCustomerModalOpen(false);
          loadCustomers();
        }}
        businessId={selectedBusiness?.id || ''}
      />

      <CustomerDetailsModal
        isOpen={isCustomerDetailsModalOpen}
        onClose={() => {
          setIsCustomerDetailsModalOpen(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
        onUpdate={loadCustomers}
      />

      {/* CSV Import Modal with Glassmorphism Design */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
             style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
          <div className="backdrop-blur-xl bg-white/95 rounded-2xl border border-purple-200/50 shadow-2xl max-w-md w-full overflow-hidden"
               style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <div className="px-6 py-4 border-b border-purple-200/30 bg-purple-50/30 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Import Customers</h2>
              <button 
                onClick={toggleImportModal}
                className="flex items-center justify-center w-8 h-8 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100/80 transition-all duration-200 touch-manipulation"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm font-medium text-gray-600 mb-6">
                Upload a CSV file with customer information. The system will automatically detect the format.
              </p>
              
              {isImporting ? (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-bold text-gray-700">Importing...</span>
                    <span className="text-sm font-bold text-purple-600">{importProgress}%</span>
                  </div>
                  <div className="h-3 bg-purple-100/80 rounded-xl mb-3 overflow-hidden">
                    <div
                      className="h-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl transition-all duration-300 ease-out"
                      style={{ width: `${importProgress}%` }}
                    ></div>
                  </div>
                  <div className="text-sm font-medium text-gray-600">
                    Processed: {importStats.imported + importStats.duplicates + importStats.errors}/{importStats.total}
                    {importStats.duplicates > 0 && (
                      <span className="ml-2 text-orange-600 font-bold">• {importStats.duplicates} duplicates skipped</span>
                    )}
                    {importStats.errors > 0 && (
                      <span className="ml-2 text-red-600 font-bold">• {importStats.errors} errors</span>
                    )}
                  </div>
                </div>
              ) : importCompleted ? (
                <div>
                  <div className="mb-6">
                    <h3 className="text-sm font-bold text-gray-800 mb-3">
                      {importStats.imported > 0 
                        ? "Import Summary" 
                        : importStats.errors > 0 
                          ? "Import Failed" 
                          : "No Customers Imported"}
                    </h3>
                    <div className="bg-purple-50/50 backdrop-blur-sm p-4 rounded-2xl border border-purple-200/30"
                         style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="text-gray-600 font-medium">Total records:</div>
                        <div className="text-gray-900 font-bold">{importStats.total}</div>
                        
                        <div className="text-gray-600 font-medium">Successfully imported:</div>
                        <div className="text-green-600 font-bold">{importStats.imported}</div>
                        
                        <div className="text-gray-600 font-medium">Duplicates skipped:</div>
                        <div className="text-orange-500 font-bold">{importStats.duplicates}</div>
                        
                        <div className="text-gray-600 font-medium">Errors:</div>
                        <div className="text-red-500 font-bold">{importStats.errors}</div>
                      </div>
                      
                      {importStats.duplicates > 0 && (
                        <div className="mt-3 text-sm font-medium text-orange-600">
                          <p>Duplicate records were skipped to prevent overwriting existing customers.</p>
                        </div>
                      )}
                      
                      {importStats.errors > 0 && (
                        <div className="mt-3 text-sm font-medium text-red-600">
                          <p>Some records couldn't be imported due to validation errors or missing required fields.</p>
                        </div>
                      )}
                      
                      {importStats.imported === 0 && importStats.errors === 0 && importStats.duplicates > 0 && (
                        <div className="mt-3 text-sm font-medium text-orange-600">
                          <p>All customers already exist in the system.</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={toggleImportModal}
                      className="px-6 py-3 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-bold text-gray-700 mb-2">CSV File</label>
                    <div className="flex items-center space-x-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-3 text-sm font-bold text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl hover:bg-gray-50/80 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 ease-out shadow-sm touch-manipulation flex-1"
                        style={{ 
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)'
                        }}
                      >
                        {importFile ? importFile.name : 'Select CSV file'}
                      </button>
                      {importFile && (
                        <button
                          onClick={() => setImportFile(null)}
                          className="px-3 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 bg-gray-100/80 hover:bg-gray-200/80 rounded-xl transition-all duration-200 touch-manipulation"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-bold text-gray-700 mb-2">Delimiter</label>
                    <div className="flex gap-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value=","
                          checked={csvDelimiter === ','}
                          onChange={() => setCsvDelimiter(',')}
                          className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Comma (,)</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value=";"
                          checked={csvDelimiter === ';'}
                          onChange={() => setCsvDelimiter(';')}
                          className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Semicolon (;)</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          value="\t"
                          checked={csvDelimiter === '\t'}
                          onChange={() => setCsvDelimiter('\t')}
                          className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Tab</span>
                      </label>
                    </div>
                    <p className="text-sm font-medium text-gray-500 mt-2">Auto-detection will be attempted if needed</p>
                  </div>
                  
                  <div className="bg-purple-50/50 backdrop-blur-sm p-4 rounded-2xl mb-6 border border-purple-200/30"
                       style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-sm font-bold text-gray-700">Expected CSV Format:</h3>
                      <button 
                        onClick={downloadSampleCSV}
                        className="text-sm font-bold text-purple-600 hover:text-purple-800 underline transition-colors duration-200"
                      >
                        Download Sample
                      </button>
                    </div>
                    <pre className="text-sm font-mono text-gray-600 whitespace-pre-wrap bg-gray-100/50 p-2 rounded-xl">
                      first_name,last_name,email,phone,address
                    </pre>
                    <p className="text-sm font-medium text-gray-500 mt-2">
                      Only first_name and last_name are required. Email and phone must be unique.
                    </p>
                  </div>
                  
                  <div className="bg-blue-50/50 backdrop-blur-sm p-4 rounded-2xl mb-6 border border-blue-200/30"
                       style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                    <h3 className="text-sm font-bold text-gray-700 mb-2">📋 Common Import Errors & How We Check Duplicates:</h3>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li><strong>Missing required fields:</strong> First name and last name are required</li>
                      <li><strong>Duplicate phone numbers:</strong> We first check if phone number already exists</li>
                      <li><strong>Duplicate emails:</strong> If phone is unique, we then check if email already exists</li>
                      <li><strong>File format issues:</strong> Make sure file is saved as .csv (not .xlsx)</li>
                      <li><strong>Character encoding:</strong> Save with UTF-8 encoding for special characters</li>
                      <li><strong>Empty rows:</strong> Remove any blank rows from your CSV file</li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2 font-medium">
                      💡 Tip: Check the browser console (F12) for detailed error messages
                    </p>
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={toggleImportModal}
                      disabled={isImporting}
                      className="px-6 py-3 text-sm font-bold text-gray-600 bg-gray-100/80 hover:bg-gray-200/80 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Import button clicked in modal');
                        if (!importFile) {
                          toast.error('Please select a CSV file');
                          return;
                        }
                        if (!selectedBusiness?.id) {
                          toast.error('No business selected');
                          return;
                        }
                        toast.info('Processing CSV import...');
                        // Use setTimeout to ensure the UI updates before starting the import process
                        setTimeout(() => {
                          handleImportCustomers();
                        }, 100);
                      }}
                      type="button"
                      disabled={!importFile || isImporting}
                      className="px-6 py-3 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation disabled:opacity-50"
                    >
                      {isImporting ? 'Importing...' : 'Import'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap with PermissionGuard
function CustomersWithPermission() {
  return (
    <PermissionGuard permission="customers.pageAccess">
      <Customers />
    </PermissionGuard>
  );
}

export default CustomersWithPermission;